import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Flame, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CountrySelect } from "@/components/country-select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { myListingQuery } from "@/lib/queries";
import { uploadImage } from "@/lib/uploads";
import { formatUsd } from "@/lib/behot";
import { tagOutboundHref } from "@/lib/outbound";
import {
  consumeEditPass,
  createEditCheckout,
  getEditPasses,
} from "@/lib/boosts.functions";
import { privatePageMetadata } from "@/lib/seo";

const EDIT_FEE_CENTS = 1299;


export const Route = createFileRoute("/app/listings/$id")({
  head: () =>
    privatePageMetadata(
      "/app/listings",
      "Listing | BeHot.Now",
      "Manage your listing.",
    ),
  component: EditListing,
});

function EditListing() {
  const { id } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: listing, isLoading } = useQuery(myListingQuery(id));

  const [form, setForm] = useState({
    name: "",
    headline: "",
    short_description: "",
    long_description: "",
    website_url: "",
    city: "",
    country_code: "",
    founded_year: "",
    team_size: "",
    pricing_note: "",
    tags: "",
  });
  const [logo, setLogo] = useState("");
  const [cover, setCover] = useState("");
  const [saving, setSaving] = useState(false);
  const [buying, setBuying] = useState(false);

  const { data: passes, refetch: refetchPasses } = useQuery({
    queryKey: ["edit-passes", id, user?.id],
    queryFn: () => getEditPasses({ data: { listingId: id } }),
    enabled: Boolean(user?.id),
  });


  useEffect(() => {
    if (!listing) return;
    setForm({
      name: listing.name ?? "",
      headline: listing.headline ?? "",
      short_description: listing.short_description ?? "",
      long_description: listing.long_description ?? "",
      website_url: listing.website_url ?? "",
      city: listing.city ?? "",
      country_code: listing.country_code ?? "",
      founded_year: listing.founded_year ? String(listing.founded_year) : "",
      team_size: listing.team_size ?? "",
      pricing_note: listing.pricing_note ?? "",
      tags: (listing.tags ?? []).join(", "),
    });
    setLogo(listing.logo_url ?? "");
    setCover(listing.cover_url ?? "");
  }, [listing]);

  async function handleUpload(file: File | undefined, kind: "logo" | "cover") {
    if (!file || !user) return;
    try {
      const url = await uploadImage(file, user.id, kind);
      if (kind === "logo") setLogo(url);
      else setCover(url);
      toast.success(`${kind === "logo" ? "Logo" : "Cover"} uploaded, save to apply.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed.");
    }
  }

  async function buyEdit() {
    setBuying(true);
    try {
      const { url } = await createEditCheckout({
        data: { listingId: id, origin: window.location.origin },
      });
      window.location.href = tagOutboundHref(url);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not start the edit checkout.");
      setBuying(false);
    }
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!passes || passes.available < 1) {
      toast.error(`Each saved edit costs ${formatUsd(EDIT_FEE_CENTS)}. Buy an edit to continue.`);
      return;
    }
    setSaving(true);
    try {
      await consumeEditPass({ data: { listingId: id } });
      const year = Number.parseInt(form.founded_year, 10);
      const { error } = await supabase
        .from("listings")
        .update({
          name: form.name.trim(),
          headline: form.headline.trim() || null,
          short_description: form.short_description.trim() || null,
          long_description: form.long_description.trim() || null,
          website_url: form.website_url.trim() || null,
          city: form.city.trim() || null,
          country_code: form.country_code.trim().toUpperCase().slice(0, 2) || null,
          founded_year: Number.isFinite(year) ? year : null,
          team_size: form.team_size.trim() || null,
          pricing_note: form.pricing_note.trim() || null,
          tags: form.tags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean),
          logo_url: logo || null,
          cover_url: cover || null,
        })
        .eq("id", id);
      if (error) throw error;
      await refetchPasses();
      await queryClient.invalidateQueries({ queryKey: ["my-listing", id] });
      await queryClient.invalidateQueries({ queryKey: ["my-listings", user?.id] });
      toast.success("Listing updated.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save the listing.");
    } finally {
      setSaving(false);
    }
  }


  if (isLoading) return <div className="text-sm text-muted-foreground">Loading listing…</div>;

  if (!listing) {
    return (
      <div className="card-surface p-6">
        <p className="font-semibold">Listing not found</p>
        <Button variant="outline" className="mt-4" onClick={() => void navigate({ to: "/app" })}>
          Back to dashboard
        </Button>
      </div>
    );
  }

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div className="max-w-2xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/app">
            <ArrowLeft className="size-4" /> Dashboard
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          <Badge variant="outline">
            {listing.moderation_status === "pending" ? "in review" : listing.moderation_status}
          </Badge>
          <Button asChild variant="hot" size="sm">
            <Link to="/get-hot" search={{ listing: listing.slug, category: undefined }}>
              <Flame className="size-4" /> Add heat
            </Link>
          </Button>
        </div>
      </div>

      <form onSubmit={save} className="card-surface mt-4 space-y-5 p-6">
        <div className="flex flex-wrap items-center gap-4">
          {logo ? (
            <img src={logo} alt="" className="size-16 rounded-2xl object-cover" />
          ) : (
            <div className="flex size-16 items-center justify-center rounded-2xl bg-accent text-xl font-bold text-accent-foreground">
              {(form.name || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex gap-2">
            <Label htmlFor="logo" className="cursor-pointer">
              <span className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted">
                <Upload className="size-4" /> Logo
              </span>
            </Label>
            <input
              id="logo"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => void handleUpload(e.target.files?.[0], "logo")}
            />
            <Label htmlFor="cover" className="cursor-pointer">
              <span className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted">
                <Upload className="size-4" /> Cover
              </span>
            </Label>
            <input
              id="cover"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => void handleUpload(e.target.files?.[0], "cover")}
            />
          </div>
        </div>

        {cover && <img src={cover} alt="" className="h-32 w-full rounded-xl object-cover" />}

        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          <Input id="name" required value={form.name} onChange={set("name")} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="headline">Headline</Label>
          <Input id="headline" value={form.headline} onChange={set("headline")} maxLength={120} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="short">Short description</Label>
          <Textarea
            id="short"
            rows={2}
            value={form.short_description}
            onChange={set("short_description")}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="long">Full description</Label>
          <Textarea
            id="long"
            rows={6}
            value={form.long_description}
            onChange={set("long_description")}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="url">Website</Label>
            <Input id="url" value={form.website_url} onChange={set("website_url")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tags">Tags (comma separated)</Label>
            <Input id="tags" value={form.tags} onChange={set("tags")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="city">City</Label>
            <Input id="city" value={form.city} onChange={set("city")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cc">Country *</Label>
            <CountrySelect
              id="cc"
              value={form.country_code}
              onChange={(v) => setForm((f) => ({ ...f, country_code: v }))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="year">Founded</Label>
            <Input id="year" inputMode="numeric" value={form.founded_year} onChange={set("founded_year")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="team">Team size</Label>
            <Input id="team" value={form.team_size} onChange={set("team_size")} placeholder="2-10" />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="pricing">Pricing note</Label>
          <Input
            id="pricing"
            value={form.pricing_note}
            onChange={set("pricing_note")}
            placeholder="Free plan · Pro from $19/mo"
          />
        </div>

        <div className="card-surface flex flex-wrap items-center justify-between gap-3 p-4">
          <div>
            <p className="font-display font-semibold">
              Paid edits available: {passes?.available ?? 0}
            </p>
            <p className="text-sm text-muted-foreground">
              Each saved change to a live listing costs {formatUsd(EDIT_FEE_CENTS)}. One edit is
              consumed per save.
            </p>
          </div>
          <Button type="button" variant="heat" onClick={() => void buyEdit()} disabled={buying}>
            <Flame className="size-4" />
            {buying ? "Opening checkout…" : `Buy an edit · ${formatUsd(EDIT_FEE_CENTS)}`}
          </Button>
        </div>

        <div className="flex gap-2">
          <Button
            type="submit"
            variant="hot"
            disabled={saving || !passes || passes.available < 1}
          >
            {saving ? "Saving…" : "Save listing"}
          </Button>
          <Button asChild variant="outline">
            <Link to="/p/$slug" params={{ slug: listing.slug }}>
              View public page
            </Link>
          </Button>
        </div>

      </form>
    </div>
  );
}
